// src/components/admin/goal/steps/Step2.js

import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Stack,
  IconButton,
  Paper,
} from '@mui/material';
import 'dayjs/locale/pt-br';
import { Add, Remove } from '@mui/icons-material';
import dayjs from 'dayjs';
import weekday from 'dayjs/plugin/weekday';
import updateLocale from 'dayjs/plugin/updateLocale';
import StepNavigation from './StepNavigation';

dayjs.extend(weekday);
dayjs.extend(updateLocale);
dayjs.locale('pt-br');

export default function Step2({ goal, setGoal, onBack, onNext, activeStep }) {
  const inicio = dayjs(goal.start_date);
  const fim = dayjs(goal.end_date);
  const diasTotais = fim.diff(inicio, 'day') + 1;

  const [numPeriodos, setNumPeriodos] = useState(goal.periods?.length || 1);
  const [periodos, setPeriodos] = useState([]);

  // Distribui os períodos inicialmente ou quando numPeriodos muda
  useEffect(() => {
    const inicio = dayjs(goal.start_date);

    const base = Math.floor(diasTotais / numPeriodos);
    const sobra = diasTotais % numPeriodos;

    const novaDistribuicao = Array.from(
      { length: numPeriodos },
      (_, i) => base + (i < sobra ? 1 : 0)
    );

    let atual = inicio;
    const lista = novaDistribuicao.map((dias, i) => {
      const inicioPeriodo = atual;
      const fimPeriodo = atual.add(dias - 1, 'day');
      atual = fimPeriodo.add(1, 'day');
      return {
        id: i + 1,
        dias,
        start_date: inicioPeriodo.format('YYYY-MM-DD'),
        end_date: fimPeriodo.format('YYYY-MM-DD'),
      };
    });

    setPeriodos(lista);
  }, [numPeriodos, diasTotais, goal.start_date, goal.end_date]);

  // Atualiza o JSON central sempre que periodos mudar
  useEffect(() => {
    setGoal((prev) => ({
      ...prev,
      periods: periodos.map((p) => ({
        period: p.id,
        start_date: p.start_date,
        end_date: p.end_date,
      })),
    }));
  }, [periodos, setGoal]);

  const ajustarDias = (index, delta) => {
    const copia = [...periodos];

    if (delta === -1) {
      if (copia[index].dias <= 1) return;

      const targetIndex = index < copia.length - 1 ? index + 1 : index - 1;
      if (!copia[targetIndex]) return;

      copia[index].dias -= 1;
      copia[targetIndex].dias += 1;
    }

    if (delta === 1) {
      const targetIndex = index < copia.length - 1 ? index + 1 : index - 1;
      if (!copia[targetIndex] || copia[targetIndex].dias <= 1) return;

      copia[index].dias += 1;
      copia[targetIndex].dias -= 1;
    }

    // Recalcular datas
    let atual = inicio;
    copia.forEach((p) => {
      const start = atual;
      const end = atual.add(p.dias - 1, 'day');
      p.start_date = start.format('YYYY-MM-DD');
      p.end_date = end.format('YYYY-MM-DD');
      p.inicio = start;
      p.fim = end;
      atual = end.add(1, 'day');
    });

    setPeriodos(copia);
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4 }}>
      <Typography variant="h6" gutterBottom>
        Em quantos períodos você quer dividir a meta?
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Escolha quantos períodos deseja dividir sua meta. Depois ajuste os dias se desejar.
      </Typography>

      <ToggleButtonGroup
        color="primary"
        exclusive
        value={numPeriodos}
        onChange={(_, value) => value && setNumPeriodos(value)}
        sx={{ mb: 4, flexWrap: 'wrap', backgroundColor: 'white'}}
      >
        {Array.from({ length: 9 }, (_, i) => (
          <ToggleButton key={i + 1} value={i + 1}>
            {i + 1}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <Typography variant="h6" sx={{ mb: 2 }}>
        Quantos dias em cada período?
      </Typography>

      <Stack spacing={2}>
        {periodos.map((p, index) => (
          <Paper key={p.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              justifyContent="space-between"
            >
              <Box>
                <Typography fontWeight="medium">{p.id}º Período</Typography>
                <Typography variant="body2" color="text.secondary">
                  {dayjs(p.start_date).format('DD/MM/YY')} (
                  {dayjs(p.start_date)
                    .format('dddd')
                    .replace(/^\w/, (c) => c.toUpperCase())}
                  )
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {dayjs(p.end_date).format('DD/MM/YY')} (
                  {dayjs(p.end_date)
                    .format('dddd')
                    .replace(/^\w/, (c) => c.toUpperCase())}
                  )
                </Typography>
              </Box>

              <Stack direction="row" alignItems="center" spacing={1}>
                <IconButton
                  onClick={() => ajustarDias(index, -1)}
                  disabled={p.dias <= 1 || index + 1 === periodos.length}
                >
                  <Remove />
                </IconButton>
                <Typography>{p.dias} dias</Typography>
                <IconButton
                  onClick={() => ajustarDias(index, 1)}
                  disabled={
                    periodos.find((_, i) => i !== index && periodos[i].dias > 1) ==
                      null || index + 1 === periodos.length
                  }
                >
                  <Add />
                </IconButton>
              </Stack>
            </Stack>
          </Paper>
        ))}
      </Stack>
      
      <StepNavigation 
        activeStep={activeStep}
        onBack={onBack}
        onNext={onNext}
      />
    </Box>
  );
}
